import type { User } from "@crm/access/authorization/types";
import { LRUCache } from "lru-cache";

/**
 * 用户信息缓存配置
 *
 * 使用 LRU (Least Recently Used) 缓存策略
 * 减少服务器函数中的数据库查询次数
 */
const USER_CACHE_CONFIG = {
  // 最大缓存用户数量
  max: 1000,

  // 缓存有效期（60秒）
  // 平衡了性能和数据新鲜度：
  // - 60秒内的重复请求直接从缓存读取
  // - 60秒后自动失效，重新从数据库获取
  ttl: 60 * 1000,

  // 允许过期数据（用于降级处理）
  // 如果数据库查询失败，可以临时返回过期数据
  allowStale: false,

  // 更新访问时间（LRU 算法核心）
  // 每次访问时更新，确保常用数据不被淘汰
  updateAgeOnGet: true,

  // 自动删除过期条目
  // 定期清理过期缓存，释放内存
  ttlAutopurge: true,
} as const;

/**
 * 用户信息缓存实例
 *
 * 全局单例，在服务器端共享
 * 键：用户 ID (string)
 * 值：完整的用户对象 (User)
 */
export const userCache = new LRUCache<string, User>(USER_CACHE_CONFIG);

/**
 * 缓存统计信息
 *
 * 用于监控缓存性能和调优
 */
type CacheStats = {
  hits: number;
  misses: number;
  size: number;
  hitRate: number;
};

let cacheHits = 0;
let cacheMisses = 0;

/**
 * 从缓存获取用户信息
 *
 * @param userId 用户 ID
 * @returns 用户对象，如果缓存中没有则返回 undefined
 */
export function getUserFromCache(userId: string): User | undefined {
  const user = userCache.get(userId);

  if (user) {
    cacheHits++;
  } else {
    cacheMisses++;
  }

  return user;
}

/**
 * 将用户信息写入缓存
 *
 * @param userId 用户 ID
 * @param user 用户对象
 */
export function setUserToCache(userId: string, user: User): void {
  userCache.set(userId, user);
}

/**
 * 使特定用户的缓存失效
 *
 * 使用场景：
 * - 用户信息更新后
 * - 用户被禁用/启用后
 * - 用户角色变更后
 *
 * @param userId 用户 ID
 */
export function invalidateUserCache(userId: string): void {
  userCache.delete(userId);
}

/**
 * 清空所有用户缓存
 *
 * 使用场景：
 * - 批量用户操作后
 * - 系统升级/重启前
 * - 安全事件发生时
 */
export function clearUserCache(): void {
  userCache.clear();
  cacheHits = 0;
  cacheMisses = 0;
}

/**
 * 获取缓存统计信息
 *
 * @returns 缓存命中率、大小等统计数据
 */
export function getCacheStats(): CacheStats {
  const total = cacheHits + cacheMisses;
  const hitRate = total > 0 ? (cacheHits / total) * 100 : 0;

  return {
    hits: cacheHits,
    misses: cacheMisses,
    size: userCache.size,
    hitRate: Math.round(hitRate * 100) / 100,
  };
}

/**
 * 重置缓存统计信息
 *
 * 用于性能测试和监控
 */
export function resetCacheStats(): void {
  cacheHits = 0;
  cacheMisses = 0;
}

/**
 * 预热缓存
 *
 * 在服务器启动时预加载常用用户信息
 * 可选功能，用于进一步优化首次请求性能
 *
 * @param users 要预加载的用户列表
 */
export function warmupCache(users: User[]): void {
  for (const user of users) {
    setUserToCache(user.id, user);
  }
}

/**
 * 开发环境下手动启用定期缓存统计输出。
 *
 * 说明：
 * - Cloudflare Workers 禁止在全局作用域使用定时器
 * - 仅在本地 Node/Bun 服务器中需要调试时调用
 */
export function startUserCacheDebugLogging(): void {
  const isWorkerRuntime =
    typeof globalThis !== "undefined" &&
    typeof (globalThis as { WebSocketPair?: unknown }).WebSocketPair === "function";

  if (!import.meta.env.DEV || isWorkerRuntime || typeof setInterval !== "function") {
    return;
  }

  setInterval(() => {
    // 缓存统计已禁用 - 保留定时器供未来启用
    void getCacheStats();
  }, 60_000); // 每分钟输出一次
}
